import { httpsCallable } from 'firebase/functions'

import { functions } from '@/firebase'
import { STUDENT_LEVELS_OPTIONS } from '@/models/card-activity/card-activity.model'

interface GenerateStructuredOutputResponse {
  results: {
    title: string
    criteria: string
  }
}

const generateStructuredOutput = httpsCallable<{ prompt: string; schema: any }, GenerateStructuredOutputResponse>(
  functions,
  'generateStructuredOutput',
)

export interface GradingCriteriaParams {
  prompt: string
  level: string
  focus: 'language' | 'correctness' | 'both'
  instructions: string
}

const generateGradingCriteria = async ({ prompt, level, focus, instructions }: GradingCriteriaParams) => {
  const description = STUDENT_LEVELS_OPTIONS.find(option => option.value === level)?.description || ''

  const ai_prompt = `
          Generate pass/fail criteria to evaluate a response to this prompt: "${prompt}"

          ${level && (focus === 'language' || focus === 'both') ? `Student Level: "${description}"` : ''}
          ${instructions ? `Teacher Instructions: "${instructions}"` : ''}
          Focus: ${focus.toUpperCase()}

          Guidelines:
          ${
            focus === 'language' || focus === 'both'
              ? `• Assess language at ${description || 'the appropriate'} level
          • Include specific grammar/vocabulary expectations
          • Specify verb tenses, sentence structures, or complexity requirements`
              : ''
          }
          
          ${
            focus === 'correctness' || focus === 'both'
              ? `• Assess factual accuracy and completeness
          • For simple answers (fill-in-blank, translations, single words):
             - List ALL acceptable variations of the correct answer
             - Specify if partial answers are acceptable
             - For translations, note if alternate translations are acceptable
             - For fill-in-the-blank, ONLY require the missing word(s), not the entire sentence
          • Clearly state correct answers if applicable (REQUIRED)`
              : ''
          }

          IMPORTANT:
          • PRIORITIZE incorporating the teacher's instructions into your criteria
          ${instructions ? `• Your criteria MUST directly address: "${instructions}"` : ''}
          • Make criteria HIGHLY SPECIFIC to this exact prompt type
          • For short answer prompts, give exact expected responses
          • For fill-in-the-blank, list all acceptable answers (just the missing parts, not full sentences: explicitly mention they aren't required to be complete sentences)
          • Specify exactly what content must be included based on the prompt
          ${focus === 'language' || focus === 'both' ? "• Tailor requirements to the student's proficiency level" : ''}
          • Keep criteria extremely concise using bullet points
          • IF the teacher provides a length requirement, this is the minimum length of the response (not the exact length)

          JSON FORMAT
          { 
            "title": "Create a title for a teacher to understand at a glance what the criteria is for",
            "criteria": "Plain text only response with line breaks for each criteria.
                    "Pass criteria:" (2-3 bullet points)
                    "Fail criteria:" (2-3 bullet points)
                    Keep under 40 words total.",
          }
              `

  try {
    const { data } = await generateStructuredOutput({
      prompt: ai_prompt,
      schema: {
        type: 'object',
        properties: {
          title: { type: 'string' },
          criteria: { type: 'string' },
        },
        required: ['title', 'criteria'],
      },
    })

    if (!data?.results) {
      throw new Error('No results returned from generateStructuredOutput')
    }

    return data.results
  } catch (error) {
    console.error('Error generating grading criteria:', error)

    return {
      error: true,
      message: error instanceof Error ? error.message : 'Error generating grading criteria',
    }
  }
}

export default generateGradingCriteria

// /**
//  * Generates grading criteria based on specified parameters
//  * @param params - Configuration for generating the criteria
//  * @returns Generated grading criteria as a string
//  */
// const generateGradingCriteria = async ({
//   prompt,
//   level,
//   focus,
//   instructions
// }: GradingCriteriaParams) => {
//   const description = STUDENT_LEVELS_OPTIONS.find(option => option.value === level)?.description || ''

//   const ai_data = {
//     chat: {
//       model: 'gpt-4o',
//       temperature: 1.0,
//       messages: [
//         {
//           role: 'user',
//           content: [
//             {
//               type: 'text',
//               text: `
//           Generate pass/fail criteria to evaluate a response to this prompt: "${prompt}"

//           ${level && (focus === 'language' || focus === 'both') ? `Student Level: "${description}"` : ''}
//           ${instructions ? `Teacher Instructions: "${instructions}"` : ''}
//           Focus: ${focus.toUpperCase()}

//           Guidelines:
//           ${focus === 'language' || focus === 'both'
//             ? `• Assess language at ${description || 'the appropriate'} level
//           • Include specific grammar/vocabulary expectations
//           • Specify verb tenses, sentence structures, or complexity requirements`
//             : ''}

//           ${focus === 'correctness' || focus === 'both'
//             ? `• Assess factual accuracy and completeness
//           • For simple answers (fill-in-blank, translations, single words):
//              - List ALL acceptable variations of the correct answer
//              - Specify if partial answers are acceptable
//              - For translations, note if alternate translations are acceptable
//              - For fill-in-the-blank, ONLY require the missing word(s), not the entire sentence
//           • Clearly state correct answers if applicable (REQUIRED)`
//             : ''}

//           IMPORTANT:
//           • PRIORITIZE incorporating the teacher's instructions into your criteria
//           ${instructions ? `• Your criteria MUST directly address: "${instructions}"` : ''}
//           • Make criteria HIGHLY SPECIFIC to this exact prompt type
//           • For short answer prompts, give exact expected responses
//           • For fill-in-the-blank, list all acceptable answers (just the missing parts, not full sentences: explicitly mention they aren't required to be complete sentences)
//           • Specify exactly what content must be included based on the prompt
//           ${focus === 'language' || focus === 'both' ? '• Tailor requirements to the student\'s proficiency level' : ''}
//           • Keep criteria extremely concise using bullet points

//           FORMAT Plain text only response.
//                   "criteria": "Plain text only response.
//                   "Pass criteria:" (2-3 bullet points)
//                   "Fail criteria:" (2-3 bullet points)
//                   Keep under 40 words total.",
//               `,
//             },
//           ],
//         },
//       ],
//     },
//     response_format: {
//       type: 'json_object',
//     },
//   }

//   try {
//     // @ts-ignore - Using ts-ignore for now to bypass type checking on the return value of
//     const {data}= await (ai_data)

//     // Extract the content from the response in a simpler way
//       // eslint-disable-next-line @typescript-eslint/no-unsafe-return
//       return JSON.parse(data?.response?.content)

//     return 'Error: Unable to generate criteria'
//   } catch (error) {
//     console.error('Error generating grading criteria:', error)

//     return {
//       error: true,
//       message: error instanceof Error ? error.message : 'Error generating grading criteria'
//     }
//   }
// }

// export default generateGradingCriteria
